import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { storageService } from '../../../services/storageService';

const InContentImageUpload = ({ onImageInsert, postId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [altText, setAltText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    files?.forEach(file => handleFileUpload(file));
  };

  const handleFileUpload = async (file) => {
    if (!file?.type?.startsWith('image/')) {
      setUploadError('Please upload an image file');
      return;
    }

    if (file?.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      // Generate temporary post ID if creating new post
      const tempPostId = postId || `temp-${Date.now()}`;
      
      const result = await storageService?.uploadContentImage(tempPostId, file);
      
      const newImage = {
        id: Date.now() + Math.random(),
        url: result?.url,
        alt: '',
        name: file?.name,
        size: file?.size,
        path: result?.path
      };
      
      setUploadedImages(prev => [...prev, newImage]);
      setIsUploading(false);
    } catch (error) {
      setUploadError(error?.message || 'Upload failed');
      setIsUploading(false);
      console.error('Content image upload error:', error);
    }
  };

  const handleInsertImage = () => {
    if (selectedImage && altText?.trim()) {
      onImageInsert({
        ...selectedImage,
        alt: altText
      });
      setIsOpen(false);
      setSelectedImage(null);
      setAltText('');
    } else {
      alert('Please select an image and provide alt text');
    }
  };

  const handleRemoveImage = (imageId) => {
    setUploadedImages(prev => prev?.filter(img => img?.id !== imageId));
    if (selectedImage?.id === imageId) {
      setSelectedImage(null);
      setAltText('');
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base md:text-lg font-semibold text-foreground">Content Images</h3>
        <Button
          variant="outline"
          size="sm"
          iconName={isOpen ? 'ChevronUp' : 'ChevronDown'}
          iconPosition="right"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? 'Hide' : 'Show'} Gallery
        </Button>
      </div>
      {isOpen && (
        <div className="space-y-4 md:space-y-6">
          <div className="border-2 border-dashed border-border rounded-lg p-6 md:p-8 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {isUploading ? (
              <div className="space-y-3">
                <Icon name="Upload" size={40} className="mx-auto text-primary animate-pulse" />
                <p className="text-sm text-muted-foreground">Uploading images...</p>
              </div>
            ) : (
              <div className="space-y-3">
                <Icon name="ImagePlus" size={40} className="mx-auto text-muted-foreground" />
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">Upload content images</p>
                  <p className="text-xs text-muted-foreground">
                    Select multiple images to add to your post
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef?.current?.click()}
                >
                  Browse Files
                </Button>
              </div>
            )}
          </div>

          {uploadedImages?.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-foreground">Uploaded Images</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {uploadedImages?.map((image) => (
                  <div
                    key={image?.id}
                    className={`relative group cursor-pointer border-2 rounded-lg overflow-hidden transition-all duration-250 ${
                      selectedImage?.id === image?.id
                        ? 'border-primary' :'border-border hover:border-muted-foreground'
                    }`}
                    onClick={() => {
                      setSelectedImage(image);
                      setAltText(image?.alt);
                    }}
                  >
                    <div className="aspect-square">
                      <Image
                        src={image?.url}
                        alt={image?.alt || 'Content image thumbnail showing uploaded media for blog post insertion'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleRemoveImage(image?.id);
                      }}
                      className="absolute top-2 right-2 p-1.5 bg-destructive text-destructive-foreground rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-250"
                      aria-label="Remove image"
                    >
                      <Icon name="Trash2" size={14} color="currentColor" />
                    </button>
                    {selectedImage?.id === image?.id && (
                      <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
                        <Icon name="Check" size={24} className="text-primary" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedImage && (
            <div className="space-y-4 p-4 bg-muted rounded-lg">
              <h4 className="text-sm font-medium text-foreground">Selected Image Details</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-foreground mb-1 block">
                    Alt Text (Required)
                  </label>
                  <input
                    type="text"
                    value={altText}
                    onChange={(e) => setAltText(e?.target?.value)}
                    placeholder="Describe the image for accessibility"
                    className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {selectedImage?.name} • {(selectedImage?.size / 1024)?.toFixed(2)} KB
                </p>
                {uploadError && (
                  <p className="text-xs text-destructive mt-2">{uploadError}</p>
                )}
                <Button
                  variant="default"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleInsertImage}
                  fullWidth
                >
                  Insert Image into Content
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InContentImageUpload;